/**
 * Utility functions for MiniCalendar component
 */

import { format, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns'
import { getDateFnsLocale } from '@vacationtracker/shared/i18n'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { MiniCalendarDay } from './types'
import { IntlShape } from 'react-intl'

/**
 * Groups calendar days into weeks respecting weekStartsOn preference
 *
 * @param days - Array of calendar days
 * @param weekStartsOn - Week start preference (0 = Sunday, 1 = Monday)
 * @returns Array of weeks, each containing 7 days (padded with empty days if needed)
 *
 * @example
 * const weeks = groupIntoWeeks(calendarDays, 1) // Monday first
 * // Returns: [[Mon, Tue, Wed, ...], [Mon, Tue, Wed, ...]]
 */
export function groupIntoWeeks(
  days: MiniCalendarDay[],
  weekStartsOn: 0 | 1
): MiniCalendarDay[][] {
  if (days.length === 0) return []

  const weeks: MiniCalendarDay[][] = []

  // Get the first and last dates
  const firstDate = days[0].date
  const lastDate = days[days.length - 1].date

  // Calculate week boundaries
  const weekStart = startOfWeek(firstDate, { weekStartsOn })
  const weekEnd = endOfWeek(lastDate, { weekStartsOn })

  // Get all days in the week range
  const allDays = eachDayOfInterval({ start: weekStart, end: weekEnd })

  // Create a map of dates to calendar days for quick lookup
  const dayMap = new Map<string, MiniCalendarDay>()
  days.forEach((day) => {
    const dateStr = format(day.date, 'yyyy-MM-dd')
    dayMap.set(dateStr, day)
  })

  // Group into weeks
  for (let i = 0; i < allDays.length; i += 7) {
    const weekDates = allDays.slice(i, i + 7)
    const week: MiniCalendarDay[] = weekDates.map((date) => {
      const dateStr = format(date, 'yyyy-MM-dd')
      const existingDay = dayMap.get(dateStr)
      if (existingDay) {
        return existingDay
      }
      // Empty day for padding
      return { date, type: 'empty' }
    })
    weeks.push(week)
  }

  return weeks
}

/**
 * Gets month labels with their span (number of days) for the first week
 *
 * @param weeks - Array of weeks containing calendar days
 * @param locale - Optional locale for month name translation
 * @returns Array of month labels with their span
 *
 * @example
 * const labels = getMonthLabels(weeks, 'en')
 * // Returns: [{ month: 'JANUARY', span: 3 }, { month: 'FEBRUARY', span: 4 }]
 */
export function getMonthLabels(
  weeks: MiniCalendarDay[][],
  locale?: LocaleEnum
): { month: string; span: number }[] {
  if (weeks.length === 0 || weeks[0].length === 0) return []

  const labels: { month: string; span: number }[] = []
  let currentMonth = ''
  let currentSpan = 0
  const dateFnsLocale = getDateFnsLocale(locale || LocaleEnum.en)

  // Process first week to get month labels
  weeks[0].forEach((day) => {
    if (day.type === 'empty') {
      currentSpan++
      return
    }

    const month = format(day.date, 'MMMM', { locale: dateFnsLocale }).toUpperCase()
    if (month !== currentMonth) {
      if (currentMonth) {
        labels.push({ month: currentMonth, span: currentSpan })
      }
      currentMonth = month
      currentSpan = 1
    } else {
      currentSpan++
    }
  })

  if (currentMonth) {
    labels.push({ month: currentMonth, span: currentSpan })
  }

  return labels
}

/**
 * Formats a tooltip string for a calendar day
 *
 * @param day - The calendar day to format
 * @param formatMessage - Optional formatMessage function for translations
 * @param locale - Optional locale for date formatting
 * @returns Formatted tooltip string
 *
 * @example
 * formatTooltip({ date: new Date('2026-01-01'), type: 'pto' }, formatMessage, 'en')
 * // Returns: "Wed, Jan 1 - PTO"
 */
export function formatTooltip(day: MiniCalendarDay, formatMessage?: IntlShape['formatMessage'], locale?: LocaleEnum): string {
  const dateFormatted = format(day.date, 'EEE, MMM d', { locale: getDateFnsLocale(locale || LocaleEnum.en) })

  if (day.label) {
    return `${dateFormatted} - ${day.label}`
  }

  switch (day.type) {
    case 'pto':
      return `${dateFormatted} - ${formatMessage ? formatMessage({ id: 'energyLevel.calendar.pto' }) : 'PTO'}`
    case 'holiday':
      return `${dateFormatted} - ${day.label || (formatMessage ? formatMessage({ id: 'energyLevel.calendar.holiday' }) : 'Holiday')}`
    case 'weekend':
      return `${dateFormatted} - ${formatMessage ? formatMessage({ id: 'energyLevel.calendar.nonWorkingDay' }) : 'Non-working day'}`
    case 'workday':
      return `${dateFormatted} - ${formatMessage ? formatMessage({ id: 'energyLevel.calendar.workDay' }) : 'Work day'}`
    default:
      return dateFormatted
  }
}
